import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Store, Upload, Phone, Mail, Globe, MapPin, Clock,
  Accessibility, Truck, Instagram, Facebook, Save, Eye,
  Tag, Building2, Sparkles, X,
} from "lucide-react";
import { ISTRES_CENTER } from "@/lib/istres";

const CATEGORIES = [
  "Alimentation", "Boulangerie · Pâtisserie", "Restaurant", "Bar · Café",
  "Bijouterie", "Habillement", "Chaussures · Maroquinerie", "Fleuriste",
  "Pharmacie", "Coiffure · Beauté", "Bien-être · Spa", "Librairie",
  "Culture · Loisirs", "Sport · Fitness", "Garage · Auto", "Informatique",
  "Maison · Décoration", "Photographe", "Hôtel · Gîte", "Autre",
];

const MAX_LOGO_BYTES = 400 * 1024;

// Downscale image client-side to <= 512px, JPEG q=0.85 → keeps payload small.
async function fileToCompressedDataURL(file, maxSide = 512, quality = 0.85) {
  if (!file) return "";
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function MerchantProfilePage() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    merchant_name: "", merchant_category: "", description: "", logo_url: "", banner_url: "",
    phone: "", public_email: "", website: "", address: "",
    lat: null, lng: null, hours: "", pmr_accessible: false,
    delivery_url: "", instagram: "", facebook: "", siret: "", tags: [],
  });
  const [tagDraft, setTagDraft] = useState("");
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  useEffect(() => {
    api.get("/merchant/profile")
      .then((r) => setProfile((p) => ({ ...p, ...r.data, tags: r.data.tags || [] })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setField = (key, val) => setProfile((p) => ({ ...p, [key]: val }));

  const handleImage = async (file, key) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 2 MB).");
      return;
    }
    try {
      const compressed = await fileToCompressedDataURL(file, key === "banner_url" ? 1200 : 512, 0.85);
      if (compressed.length > MAX_LOGO_BYTES * 1.4) {
        toast.warning("Image lourde — préférez une image plus petite pour de meilleures performances.");
      }
      setField(key, compressed);
      toast.success(key === "logo_url" ? "Logo prêt à enregistrer ✓" : "Bannière prête ✓");
    } catch (e) {
      toast.error("Impossible de lire l'image.");
    }
  };

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (profile.tags.includes(t)) { setTagDraft(""); return; }
    setProfile((p) => ({ ...p, tags: [...p.tags, t].slice(0, 8) }));
    setTagDraft("");
  };

  const removeTag = (t) => setProfile((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }));

  const save = async () => {
    if (!profile.merchant_name.trim()) {
      toast.error("Le nom du commerce est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/merchant/profile", profile);
      toast.success("Fiche mise à jour ✓ Elle est désormais visible publiquement.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="phenix-card p-10 text-center text-plum-700/60">Chargement de votre fiche…</div>;
  }

  return (
    <div className="space-y-8 max-w-5xl" data-testid="merchant-profile-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="phenix-pill bg-sun-100 text-sun-700 mb-3">
            <Sparkles size={11} strokeWidth={2.5} /> Ma fiche commerçant
          </div>
          <h1 className="heading-display text-3xl lg:text-4xl">Personnalisez votre vitrine sur Istres &amp; Moi</h1>
          <p className="mt-2 text-sm text-plum-700/70 max-w-xl">
            Renseignez votre logo, vos coordonnées, vos horaires. Les habitants découvriront votre commerce dans l'annuaire et sur la carte d'Istres.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-6"
          data-testid="merchant-profile-save"
        >
          <Save size={15} /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </header>

      {/* Logo + Bannière */}
      <section className="phenix-card overflow-hidden">
        {/* Banner zone */}
        <div className="relative h-44 bg-gradient-to-br from-plum-100 via-lavande-100 to-sun-100">
          {profile.banner_url ? (
            <img src={profile.banner_url} alt="Bannière" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-plum-700/40 text-[12px] font-semibold uppercase tracking-widest">
              Bannière de votre boutique (optionnel)
            </div>
          )}
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="merchant-banner-input"
            onChange={(e) => handleImage(e.target.files?.[0], "banner_url")}
          />
          <button
            onClick={() => bannerInputRef.current?.click()}
            className="absolute top-3 right-3 phenix-pill bg-white/90 text-plum-700 hover:bg-white text-[11px] backdrop-blur"
            data-testid="merchant-banner-upload"
          >
            <Upload size={11} strokeWidth={2.5} /> {profile.banner_url ? "Changer la bannière" : "Ajouter une bannière"}
          </button>
          {profile.banner_url && (
            <button
              onClick={() => setField("banner_url", "")}
              className="absolute top-3 right-44 phenix-pill bg-white/90 text-pink-600 hover:bg-white text-[11px] backdrop-blur"
            >
              <X size={11} strokeWidth={2.5} /> Retirer
            </button>
          )}
        </div>

        {/* Logo + main info */}
        <div className="px-6 pb-6 pt-0 relative">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 relative">
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full bg-white shadow-xl border-4 border-white overflow-hidden grid place-items-center">
                {profile.logo_url ? (
                  <img src={profile.logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <Store size={36} className="text-plum-700/30" strokeWidth={1.6} />
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="merchant-logo-input"
                onChange={(e) => handleImage(e.target.files?.[0], "logo_url")}
              />
              <button
                onClick={() => logoInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-plum-700 hover:bg-plum-800 text-white grid place-items-center shadow-md"
                title="Téléverser votre logo"
                data-testid="merchant-logo-upload"
              >
                <Upload size={13} strokeWidth={2.5} />
              </button>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-3 pt-2">
              <Field label="Nom du commerce *">
                <Input
                  data-testid="merchant-name-input"
                  value={profile.merchant_name}
                  onChange={(e) => setField("merchant_name", e.target.value)}
                  placeholder="Boulangerie du Soleil"
                  className="h-11 rounded-xl"
                />
              </Field>
              <Field label="Catégorie">
                <select
                  data-testid="merchant-category-select"
                  value={profile.merchant_category}
                  onChange={(e) => setField("merchant_category", e.target.value)}
                  className="w-full h-11 rounded-xl border border-cream-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-plum-500/40"
                >
                  <option value="">— Choisir —</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="phenix-card p-6">
        <SectionTitle icon={Store} title="Présentation de votre commerce" />
        <Textarea
          data-testid="merchant-description-input"
          value={profile.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="Pâtisserie artisanale au cœur d'Istres depuis 1985. Pains au levain, fougasses aux olives, viennoiseries…"
          rows={5}
          className="rounded-xl resize-none"
          maxLength={600}
        />
        <div className="mt-2 text-[11px] text-plum-700/50 text-right tabular-nums">
          {profile.description.length}/600
        </div>
      </section>

      {/* Coordonnées */}
      <section className="phenix-card p-6">
        <SectionTitle icon={Phone} title="Coordonnées" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Téléphone" icon={Phone}>
            <Input data-testid="merchant-phone-input" value={profile.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="04 42 56 12 34" className="h-11 rounded-xl" />
          </Field>
          <Field label="Email public" icon={Mail}>
            <Input data-testid="merchant-email-input" type="email" value={profile.public_email} onChange={(e) => setField("public_email", e.target.value)} placeholder="contact@maboutique.fr" className="h-11 rounded-xl" />
          </Field>
          <Field label="Site web" icon={Globe}>
            <Input data-testid="merchant-website-input" value={profile.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://www.maboutique.fr" className="h-11 rounded-xl" />
          </Field>
          <Field label="SIRET" icon={Building2}>
            <Input data-testid="merchant-siret-input" value={profile.siret} onChange={(e) => setField("siret", e.target.value)} placeholder="123 456 789 00012" className="h-11 rounded-xl" />
          </Field>
        </div>
      </section>

      {/* Localisation */}
      <section className="phenix-card p-6">
        <SectionTitle icon={MapPin} title="Localisation" />
        <Field label="Adresse complète" icon={MapPin}>
          <Input data-testid="merchant-address-input" value={profile.address} onChange={(e) => setField("address", e.target.value)} placeholder="12 Place Champollion, 13800 Istres" className="h-11 rounded-xl" />
        </Field>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Field label="Latitude">
            <Input data-testid="merchant-lat-input" type="number" step="0.000001" value={profile.lat ?? ""} onChange={(e) => setField("lat", e.target.value ? parseFloat(e.target.value) : null)} placeholder="43.5128" className="h-11 rounded-xl" />
          </Field>
          <Field label="Longitude">
            <Input data-testid="merchant-lng-input" type="number" step="0.000001" value={profile.lng ?? ""} onChange={(e) => setField("lng", e.target.value ? parseFloat(e.target.value) : null)} placeholder="4.9889" className="h-11 rounded-xl" />
          </Field>
          <Field label=" ">
            <Button
              variant="outline"
              onClick={() => {
                setField("lat", ISTRES_CENTER.lat);
                setField("lng", ISTRES_CENTER.lng);
                toast.info("Centre d'Istres pré-rempli — affinez ensuite.");
              }}
              className="h-11 w-full rounded-xl border-cream-200 text-[12px]"
            >
              <MapPin size={13} /> Centre d'Istres
            </Button>
          </Field>
        </div>
      </section>

      {/* Horaires & Services */}
      <section className="phenix-card p-6">
        <SectionTitle icon={Clock} title="Horaires & accessibilité" />
        <Field label="Horaires d'ouverture" icon={Clock}>
          <Input
            data-testid="merchant-hours-input"
            value={profile.hours}
            onChange={(e) => setField("hours", e.target.value)}
            placeholder="Lun–Sam · 6h30–19h30 · Fermé le dimanche"
            className="h-11 rounded-xl"
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Lien de livraison (Uber Eats, Deliveroo, etc.)" icon={Truck}>
            <Input data-testid="merchant-delivery-input" value={profile.delivery_url} onChange={(e) => setField("delivery_url", e.target.value)} placeholder="https://www.deliveroo.fr/..." className="h-11 rounded-xl" />
          </Field>
          <div className="flex items-center justify-between phenix-card p-3 bg-cream-50/60 border-cream-200">
            <div className="flex items-center gap-2">
              <Accessibility size={16} className="text-mint-600" strokeWidth={2.2} />
              <div>
                <div className="text-[13px] font-semibold text-plum-900">Accessible PMR</div>
                <div className="text-[11px] text-plum-700/60">Personnes à mobilité réduite</div>
              </div>
            </div>
            <Switch
              data-testid="merchant-pmr-switch"
              checked={profile.pmr_accessible}
              onCheckedChange={(v) => setField("pmr_accessible", v)}
            />
          </div>
        </div>
      </section>

      {/* Réseaux sociaux */}
      <section className="phenix-card p-6">
        <SectionTitle icon={Instagram} title="Réseaux sociaux" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Instagram" icon={Instagram}>
            <Input data-testid="merchant-instagram-input" value={profile.instagram} onChange={(e) => setField("instagram", e.target.value)} placeholder="https://instagram.com/maboutique" className="h-11 rounded-xl" />
          </Field>
          <Field label="Facebook" icon={Facebook}>
            <Input data-testid="merchant-facebook-input" value={profile.facebook} onChange={(e) => setField("facebook", e.target.value)} placeholder="https://facebook.com/maboutique" className="h-11 rounded-xl" />
          </Field>
        </div>
      </section>

      {/* Tags */}
      <section className="phenix-card p-6">
        <SectionTitle icon={Tag} title="Mots-clés (8 max)" />
        <div className="flex gap-2 flex-wrap mb-3" data-testid="merchant-tags-list">
          {profile.tags.map((t) => (
            <Badge key={t} className="bg-plum-100 text-plum-700 hover:bg-plum-200 cursor-pointer" onClick={() => removeTag(t)}>
              {t} <X size={11} className="ml-1" />
            </Badge>
          ))}
          {profile.tags.length === 0 && (
            <div className="text-[12px] text-plum-700/40 italic">Aucun mot-clé ajouté.</div>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="merchant-tag-input"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
            placeholder="bio, artisanal, fait-maison..."
            className="h-11 rounded-xl"
          />
          <Button onClick={addTag} disabled={profile.tags.length >= 8} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-4">
            Ajouter
          </Button>
        </div>
      </section>

      {/* Public preview */}
      <section className="phenix-card p-6 bg-cream-50/50 border-cream-200">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={14} className="text-plum-700" />
          <h3 className="font-display font-bold text-[13px] uppercase tracking-wider text-plum-700">Aperçu public</h3>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-cream-200">
          <div className="flex gap-3 mb-3">
            <div className="w-14 h-14 rounded-full bg-cream-100 grid place-items-center overflow-hidden shrink-0">
              {profile.logo_url ? <img src={profile.logo_url} className="w-full h-full object-cover" alt="" /> : <Store size={22} className="text-plum-700/30" />}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display font-bold text-plum-900 truncate">{profile.merchant_name || "Nom du commerce"}</h4>
              <div className="text-[12px] text-plum-700/60">{profile.merchant_category || "Catégorie"}</div>
              {profile.address && (
                <div className="text-[11px] text-plum-700/60 mt-0.5 flex items-center gap-1"><MapPin size={10} /> {profile.address}</div>
              )}
            </div>
          </div>
          {profile.description && (
            <p className="text-[12px] text-plum-700/80 line-clamp-2 mb-2">{profile.description}</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {profile.pmr_accessible && <Badge className="bg-mint-100 text-mint-600 hover:bg-mint-100">♿ PMR</Badge>}
            {profile.delivery_url && <Badge className="bg-sun-100 text-sun-700 hover:bg-sun-100">🚲 Livraison</Badge>}
            {profile.tags.slice(0, 3).map((t) => <Badge key={t} className="bg-cream-100 text-plum-700 hover:bg-cream-100">{t}</Badge>)}
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-12 px-7"
          data-testid="merchant-profile-save-bottom"
        >
          <Save size={15} /> {saving ? "Enregistrement..." : "Enregistrer ma fiche"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={2.5} />}
        {label}
      </Label>
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <h2 className="font-display font-bold text-lg text-plum-900 mb-4 flex items-center gap-2">
      <Icon size={16} strokeWidth={2.2} className="text-plum-700" /> {title}
    </h2>
  );
}
